import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  Droplets, Package, Container, Truck, MoreHorizontal, Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useBodies, useCreateBody, useUpdateBody } from '@/hooks/useBodies';
import { BodyForm } from './BodyForm';
import type { VehicleBody, BodyType, BodyStatus } from '@/lib/types';
import { useAuth } from '@/lib/auth';

type StatusFilter = 'all' | BodyStatus;
type TypeFilter = 'all' | BodyType;
type RentFilter = 'all' | 'rented' | 'owned';

const BODY_TYPE_ICONS: Record<BodyType, typeof Droplets> = {
  water_tanker: Droplets,
  sewage_tanker: Droplets,
  flatbed: Package,
  lowbed: Package,
  container_carrier: Container,
  tipper: Truck,
  box_van: Container,
  other: MoreHorizontal,
};

export default function BodiesPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [rentFilter, setRentFilter] = useState<RentFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<VehicleBody | null>(null);

  useEffect(() => {
    const tm = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      bodyType: typeFilter === 'all' ? undefined : typeFilter,
      isRented: rentFilter === 'all' ? undefined : rentFilter === 'rented',
      page,
      pageSize: 25,
    }),
    [search, statusFilter, typeFilter, rentFilter, page]
  );

  const { data, isLoading, isError, error, refetch } = useBodies(params);
  const createMutation = useCreateBody();
  const updateMutation = useUpdateBody();

  const bodies = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };

  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';
  const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'inactive', 'in_repair', 'sold'];

  // Calculate total monthly rent commitments (rented bodies)
  const totalMonthlyRent = bodies
    .filter((b) => b.isRented && b.status === 'active' && b.monthlyRentSar != null)
    .reduce((sum, b) => sum + Number(b.monthlyRentSar), 0);

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* Page header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <Link to="/dashboard/vehicles" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />
              {t('common.backToVehicles')}
            </Link>
            <div className="flex items-center gap-3">
              <Package className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">{t('bodies.title')}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t('bodies.subtitle')}</p>
          </div>
          <Button variant="gold" size="lg" onClick={() => setCreating(true)} className="shrink-0">
            <Plus className="h-4 w-4" />
            {t('bodies.addBody')}
          </Button>
        </div>

        {/* Total monthly rent callout */}
        {totalMonthlyRent > 0 && (
          <div className="mb-4 rounded-lg border border-gold/30 bg-gold/5 p-4 flex items-center gap-3">
            <Wrench className="h-5 w-5 text-gold shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-foreground">
                {t('bodies.monthlyRentTotal', { amount: totalMonthlyRent.toLocaleString() })}
              </p>
              <p className="text-xs text-muted-foreground">{t('bodies.monthlyRentHelp')}</p>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-card border border-border rounded-lg p-4 mb-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('bodies.searchPlaceholder')}
                className="ps-10"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value as TypeFilter); setPage(1); }}
              className="h-11 rounded-md border border-input bg-background px-3 text-base"
            >
              <option value="all">{t('bodies.filter.allTypes')}</option>
              <option value="water_tanker">{t('bodies.type.water_tanker')}</option>
              <option value="sewage_tanker">{t('bodies.type.sewage_tanker')}</option>
              <option value="flatbed">{t('bodies.type.flatbed')}</option>
              <option value="lowbed">{t('bodies.type.lowbed')}</option>
              <option value="container_carrier">{t('bodies.type.container_carrier')}</option>
              <option value="tipper">{t('bodies.type.tipper')}</option>
              <option value="box_van">{t('bodies.type.box_van')}</option>
              <option value="other">{t('bodies.type.other')}</option>
            </select>
            <select
              value={rentFilter}
              onChange={(e) => { setRentFilter(e.target.value as RentFilter); setPage(1); }}
              className="h-11 rounded-md border border-input bg-background px-3 text-base"
            >
              <option value="all">{t('bodies.filter.allOwnership')}</option>
              <option value="owned">{t('bodies.owned')}</option>
              <option value="rented">{t('bodies.rented')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
              >
                {t(`bodies.filter.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">{t('common.retry')}</Button>
            </div>
          ) : bodies.length === 0 ? (
            <div className="py-20 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all' || typeFilter !== 'all' || rentFilter !== 'all'
                  ? t('bodies.noResults')
                  : t('bodies.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">{t('bodies.emptyDescription')}</p>
              {!search && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />{t('bodies.addFirstBody')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('bodies.column.code')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('bodies.column.type')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('bodies.column.owner')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('bodies.column.ownership')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('bodies.column.mountedOn')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('bodies.column.status')}</th>
                      <th className="text-end px-4 py-3 w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {bodies.map((b) => {
                      const TypeIcon = BODY_TYPE_ICONS[b.bodyType];
                      const activeAssignment = b.assignments?.find((a) => a.effectiveTo == null);
                      return (
                        <tr
                          key={b.id}
                          onClick={() => setEditing(b)}
                          className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{b.code}</td>
                          <td className="px-4 py-3">
                            <div className="inline-flex items-center gap-1.5 text-foreground">
                              <TypeIcon className="h-4 w-4 text-muted-foreground" />
                              <span>{t(`bodies.type.${b.bodyType}`)}</span>
                            </div>
                            {b.capacityM3 != null && (
                              <div className="text-xs text-muted-foreground mt-0.5">{Number(b.capacityM3)} m³</div>
                            )}
                            {b.payloadCapacityKg != null && (
                              <div className="text-xs text-muted-foreground mt-0.5">{b.payloadCapacityKg.toLocaleString()} kg</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-foreground">{b.ownerInvestor.fullNameEn}</div>
                            <div className="text-xs text-muted-foreground font-mono">{b.ownerInvestor.code}</div>
                          </td>
                          <td className="px-4 py-3">
                            {b.isRented ? (
                              <>
                                <Badge variant="gold">{t('bodies.rented')}</Badge>
                                <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                                  {Number(b.monthlyRentSar).toLocaleString()} {t('common.sarPerMonth')}
                                </div>
                              </>
                            ) : (
                              <Badge variant="default">{t('bodies.owned')}</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {activeAssignment ? (
                              <>
                                <div className="font-mono text-xs">{activeAssignment.vehicle.plateEn}</div>
                                <div className="text-xs text-muted-foreground">{activeAssignment.vehicle.brand}</div>
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">{t('bodies.notMounted')}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={
                              b.status === 'active' ? 'active' :
                              b.status === 'in_repair' ? 'gold' :
                              'inactive'
                            }>
                              {t(`bodies.status.${b.status}`)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-end" onClick={(e) => e.stopPropagation()}>
                            <RowDeleteButton
                              endpoint={`/api/v1/bodies/${b.id}`}
                              entityKind="body"
                              label={b.code}
                              onDeleted={() => refetch()}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('bodies.showingCount', {
                    from: (page - 1) * 25 + 1,
                    to: Math.min(page * 25, total),
                    total,
                  })}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('bodies.addBody')}</SheetTitle>
            <SheetDescription>{t('bodies.addBodyDescription')}</SheetDescription>
          </SheetHeader>
          <BodyForm onSubmit={handleCreate} onCancel={() => setCreating(false)} submitLabel={t('bodies.create')} />
        </SheetContent>
      </Sheet>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing ? t(`bodies.type.${editing.bodyType}`) : ''}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{editing?.code}</SheetDescription>
          </SheetHeader>
          <BodyForm initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} submitLabel={t('common.save')} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
